import {Injectable, Injector, Type} from '@angular/core';
import {Observable, of} from 'rxjs';
import {UserService} from '../../../services/user.service';
import {EMPTY_WIDGET_TYPE_ID} from '../../../widgets/empty-widget/empty-widget-type-id';
import {EmptyWidgetSettingsDef} from '../../../widgets/empty-widget/settings';
import {Cell} from '../cell';
import {Widget} from './widget';
import {WidgetDefinition} from './widget-definition';
import {WIDGET_DESCRIPTION, WidgetDescription} from './widget-description';
import {WidgetRegistryService} from './widget-registry.service';
import type {WidgetSettings} from './widget-settings';
import type {WidgetSettingsDefinition} from './widget-settings-definition';
import type {WidgetSettingsService} from './widget-settings-service';

@Injectable()
export class WidgetFactory {

    /**
     * Widget's dependencies cache.
     * Allows to not call twice the injector.get for the same service.
     * @type {WeakMap<Type<*>, *>}
     */
    private servicesMap = new WeakMap<Type<any>, any>();

    constructor(
        private widgetRegistryService: WidgetRegistryService,
        private userService: UserService,
        private injector: Injector
    ) {}

    public createWidgetByDefinition<W extends Widget>(widgetDefinition: WidgetDefinition, cell?: Cell): W {
        let widgetDescription = this.widgetRegistryService.getWidgetDescription(widgetDefinition.widgetTypeId);
        const isWidgetSupported = !!widgetDescription && this.isWidgetAllowedForCurrentUser(widgetDescription);

        if (!isWidgetSupported) {
            widgetDefinition = this.replaceWidgetByEmptyWidgetDef(widgetDefinition);
            widgetDescription = this.widgetRegistryService.getWidgetDescription(widgetDefinition.widgetTypeId);
        }

        const settings$ = this.createSettingsByDefinition(widgetDescription, widgetDefinition.settings);
        const widget: Widget = Injector
            .create({
                name:      `${widgetDescription.widgetTypeId} Injector`,
                parent:    this.injector,
                providers: [
                    {
                        provide:  Widget,
                        useClass: Widget,
                        deps:     [Injector]
                    },
                    {
                        provide:  WIDGET_DESCRIPTION,
                        useValue: widgetDescription
                    }
                ]
            })
            .get(Widget);

        this.setWidgetCell(widget, cell);

        widget.init(
            widgetDescription,
            settings$,
            widgetDefinition.tabId,
            widgetDefinition.id,
            widgetDefinition.linkingGroupId
        );

        return widget as W;
    }

    public getWidgetDefinition(widget: Widget): WidgetDefinition {

        /**
         * [HED-282] Prevent saving of not initialized settings:
         * if the widget is in the process of applying its initial settings,
         * then we should store not yet processed settings in the layout
         * instead of empty/default settings.
         */
        const settingsDefinition: WidgetSettingsDefinition = this.getSettingsDefinition(
            widget.widgetDescription,
            widget.implementationInstance.isInitialized
                ? widget.implementationInstance.settings
                : widget.implementationInstance.pendingSettings
        );

        let widgetDef: WidgetDefinition = {
            id:             widget.id,
            tabId:          widget.tabId,
            cellId:         widget.cell && widget.cell.id,
            widgetTypeId:   widget.widgetDescription.widgetTypeId,
            settings:       settingsDefinition,
            linkingGroupId: widget.linkingGroupId
        };

        widgetDef = this.replaceEmptyWidgetByOrigDef(widgetDef);

        return widgetDef;
    }

    public setWidgetCell(widget: Widget, cell?: Cell): void {
        widget.cell = cell;

        if (cell) {
            if (!cell.widgets.includes(widget)) {
                cell.widgets.push(widget);
            }

            if (!cell.activeWidget) {
                cell.activeWidget = widget;
            }
        }
    }

    private getSettingsDefinition(
        widgetDescription: WidgetDescription,
        settings: WidgetSettings
    ): WidgetSettingsDefinition {
        const settingsService: WidgetSettingsService = this.getService(widgetDescription.widgetSettingsServiceType);

        return settings && settingsService && settingsService.toDefinition
            ? settingsService.toDefinition(settings)
            : settings && {...settings} as WidgetSettingsDefinition;
    }

    private createSettingsByDefinition(
        widgetDescription: WidgetDescription,
        settingsDefinition: WidgetSettingsDefinition
    ): Observable<WidgetSettings> {
        const settingsService: WidgetSettingsService = this.getService(widgetDescription.widgetSettingsServiceType);

        return settingsDefinition && settingsService && settingsService.fromDefinition
            ? settingsService.fromDefinition(settingsDefinition)
            : of(settingsDefinition && {...settingsDefinition} as WidgetSettings);
    }

    private getService<T>(serviceType: Type<T>): T {
        let service: T = this.servicesMap.get(serviceType);

        if (service === undefined) {
            service = this.injector.get(serviceType, null);
            this.servicesMap.set(serviceType, service);
        }

        return service;
    }

    private isWidgetAllowedForCurrentUser(widgetDescription: WidgetDescription): boolean {
        const user = this.userService.user;

        return !widgetDescription.userRoles
            || widgetDescription.userRoles.includes(user.role);
    }

    private replaceWidgetByEmptyWidgetDef(widgetDefinition: WidgetDefinition): WidgetDefinition {
        const emptyWidgetSettingsDef: EmptyWidgetSettingsDef = {
            title:                    null,
            origWidgetTypeId:         widgetDefinition.widgetTypeId,
            origWidgetSettings:       widgetDefinition.settings,
            origWidgetLinkingGroupId: widgetDefinition.linkingGroupId
        };

        return {
            ...widgetDefinition,
            widgetTypeId: EMPTY_WIDGET_TYPE_ID,
            settings:     emptyWidgetSettingsDef
        };
    }

    private replaceEmptyWidgetByOrigDef(widgetDefinition: WidgetDefinition): WidgetDefinition {
        let origWidgetDef: WidgetDefinition = widgetDefinition;

        if (widgetDefinition.widgetTypeId === EMPTY_WIDGET_TYPE_ID) {
            const emptyWidgetSettingsDef: EmptyWidgetSettingsDef = widgetDefinition.settings;

            if (emptyWidgetSettingsDef.origWidgetTypeId) {
                origWidgetDef = {
                    ...widgetDefinition,
                    widgetTypeId:   emptyWidgetSettingsDef.origWidgetTypeId,
                    settings:       emptyWidgetSettingsDef.origWidgetSettings,
                    linkingGroupId: emptyWidgetSettingsDef.origWidgetLinkingGroupId
                };
            }
        }

        return origWidgetDef;
    }

}
